// CallPanel.jsx — Desktop voice/video call UI + mobile call view + debug panel.
//
// Layout: chat panel (LEFT) | drag handle | video area (RIGHT)
// chatW range: 220–700px, drag handle resizes it.
// Bitrate auto-adjusts via rtc.setVideoBitrate based on tile count.
//
// Contains: RtcDebugPanel, ErrorBoundary, MobileCallView, CallPanel (default export)

import React, { useState, useEffect, useRef } from 'react';
import { useTheme, I } from '../constants';
import { VideoTile, DeviceSelector, useTalkingDetection } from './VideoTile';

// ── RTC event log — shared singleton so useWebRTC can push into it ────────────
export const rtcLog = {
  entries: [],
  listeners: new Set(),
  push(msg, type = 'info') {
    const ts = new Date().toLocaleTimeString('en', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    this.entries.push({ ts, msg, type });
    if (this.entries.length > 400) this.entries.shift();
    this.listeners.forEach(fn => fn());
  },
  clear() { this.entries = []; this.listeners.forEach(fn => fn()); },
};

// ── RtcDebugPanel ─────────────────────────────────────────────────────────────
function RtcDebugPanel({ rtc }) {
  const C = useTheme();
  const [, tick] = useState(0);
  const [open, setOpen]       = useState(true);
  const [tab, setTab]         = useState('log');   // 'log' | 'stats'
  const [stats, setStats]     = useState({});
  const logEndRef = useRef();

  // Subscribe to log updates
  useEffect(() => {
    const fn = () => tick(t => t + 1);
    rtcLog.listeners.add(fn);
    return () => rtcLog.listeners.delete(fn);
  }, []);

  // Auto-scroll log
  useEffect(() => {
    if (tab === 'log') logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [rtcLog.entries.length, tab]); // eslint-disable-line

  // Stats poller
  useEffect(() => {
    const pcs = rtc._peerConns?.current || {};
    if (!Object.keys(pcs).length) return;
    const iv = setInterval(async () => {
      const next = {};
      for (const [pid, pc] of Object.entries(pcs)) {
        const entry = {
          ice: pc.iceConnectionState, conn: pc.connectionState, signal: pc.signalingState,
          senders:   pc.getSenders().map(s => s.track ? `${s.track.kind}:${s.track.readyState}:${s.track.enabled ? 'on' : 'off'}` : 'no-track'),
          receivers: pc.getReceivers().map(r => r.track ? `${r.track.kind}:${r.track.readyState}:${r.track.muted ? 'muted' : 'live'}` : 'no-track'),
          stream: rtc.peers[pid]?.stream
            ? `${rtc.peers[pid].stream.getTracks().length}t (${rtc.peers[pid].stream.getTracks().map(t => t.kind[0] + ':' + (t.readyState === 'live' ? '✓' : '✗') + (t.muted ? 'M' : '')).join(' ')})`
            : 'none',
          username: rtc.peers[pid]?.username || pid.slice(0, 8),
        };
        try {
          const report = await pc.getStats();
          let bytesSent = 0, bytesRecv = 0, rtt = null, vWidth = 0, vHeight = 0, fps = 0, pktLoss = 0;
          report.forEach(s => {
            if (s.type === 'outbound-rtp') { bytesSent += s.bytesSent || 0; if (s.kind === 'video') { vWidth = s.frameWidth || 0; vHeight = s.frameHeight || 0; fps = s.framesPerSecond || 0; } }
            if (s.type === 'inbound-rtp')  { bytesRecv += s.bytesReceived || 0; pktLoss += s.packetsLost || 0; if (s.kind === 'video') { vWidth = s.frameWidth || 0; vHeight = s.frameHeight || 0; fps = Math.round(s.framesPerSecond || 0); } }
            if (s.type === 'candidate-pair' && s.state === 'succeeded') rtt = s.currentRoundTripTime;
          });
          Object.assign(entry, { bytesSent, bytesRecv, rtt: rtt !== null ? `${Math.round(rtt * 1000)}ms` : '?', vWidth, vHeight, fps, pktLoss });
        } catch {}
        next[pid] = entry;
      }
      setStats(next);
    }, 1000);
    return () => clearInterval(iv);
  }, [rtc.peers, rtc._peerConns]); // eslint-disable-line

  const copyLog = () => {
    const text = rtcLog.entries.map(e => `[${e.ts}] ${e.msg}`).join('\n');
    navigator.clipboard?.writeText(text).then(() => rtcLog.push('Log copied to clipboard', 'ok')).catch(() => rtcLog.push('Copy failed', 'err'));
  };

  const stateColor = s => ({ connected: '#22C55E', completed: '#22C55E', checking: '#F59E0B', failed: '#EF4444', disconnected: '#EF4444', closed: '#6B7280' }[s] || '#CBD5E1');
  const logColor  = t => ({ ok: '#22C55E', err: '#EF4444', warn: '#F59E0B', info: '#94A3B8' }[t] || '#94A3B8');
  const localTracks = rtc.localStream ? rtc.localStream.getTracks().map(t => `${t.kind}:${t.readyState}:${t.muted ? 'muted' : 'live'}:${t.enabled ? 'on' : 'off'}`) : [];

  const S = {
    panel: { position: 'fixed', bottom: 80, right: 8, zIndex: 9999, background: '#0A0C10', border: '1px solid #2A2D36', borderRadius: 14, width: Math.min(window.innerWidth - 16, 400), maxHeight: '70vh', display: 'flex', flexDirection: 'column', fontFamily: 'monospace', fontSize: 11, color: '#CBD5E1', boxShadow: '0 8px 40px rgba(0,0,0,0.9)' },
    hdr:   { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 12px', borderBottom: '1px solid #1A1D24', flexShrink: 0 },
    tabs:  { display: 'flex', borderBottom: '1px solid #1A1D24', flexShrink: 0 },
    tab:   active => ({ flex: 1, padding: '7px 0', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, background: 'transparent', color: active ? '#00D4AA' : '#4B5563', borderBottom: active ? '2px solid #00D4AA' : '2px solid transparent' }),
    body:  { flex: 1, overflow: 'auto', padding: '8px 10px' },
    row:   { display: 'flex', gap: 6, padding: '6px 10px', borderTop: '1px solid #1A1D24', flexShrink: 0 },
    btn:   (accent) => ({ flex: 1, padding: '6px 0', border: `1px solid ${accent ? '#00D4AA44' : '#2A2D36'}`, borderRadius: 7, background: 'transparent', color: accent ? '#00D4AA' : '#6B7280', cursor: 'pointer', fontWeight: 700, fontSize: 11 }),
  };

  if (!open) return (
    <button onClick={() => setOpen(true)} style={{ position: 'fixed', bottom: 80, right: 8, zIndex: 9999, background: '#111318', border: '1px solid #EF4444', borderRadius: 8, color: '#EF4444', padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
      📋 RTC Log ({rtcLog.entries.length})
    </button>
  );

  return (
    <div style={S.panel}>
      {/* Header */}
      <div style={S.hdr}>
        <span style={{ fontWeight: 700, color: '#00D4AA', flex: 1 }}>📋 RTC Debug Log</span>
        <span style={{ color: '#4B5563', fontSize: 10 }}>{rtcLog.entries.length} events</span>
        <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 15, padding: 0, marginLeft: 4 }}>✕</button>
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        <button style={S.tab(tab === 'log')}   onClick={() => setTab('log')}>Event Log</button>
        <button style={S.tab(tab === 'stats')} onClick={() => setTab('stats')}>Live Stats</button>
        <button style={S.tab(tab === 'device')} onClick={() => setTab('device')}>Device</button>
      </div>

      {/* Body */}
      <div style={S.body}>

        {tab === 'log' && (
          rtcLog.entries.length === 0
            ? <div style={{ color: '#4B5563', textAlign: 'center', padding: 20 }}>No events yet — join a channel</div>
            : rtcLog.entries.map((e, i) => (
                <div key={i} style={{ color: logColor(e.type), lineHeight: 1.6, borderBottom: '1px solid #0D0F14', padding: '1px 0', wordBreak: 'break-all' }}>
                  <span style={{ color: '#374151', marginRight: 6 }}>[{e.ts}]</span>{e.msg}
                </div>
              ))
        )}

        {tab === 'stats' && (
          <>
            <div style={{ marginBottom: 8, padding: '6px 8px', background: '#111318', borderRadius: 7, borderLeft: '3px solid #00D4AA' }}>
              <div style={{ color: '#00D4AA', fontWeight: 700, marginBottom: 3 }}>LOCAL TRACKS</div>
              {localTracks.length
                ? localTracks.map((t, i) => <div key={i} style={{ color: t.includes('live') ? '#22C55E' : '#EF4444' }}>  ▸ {t}</div>)
                : <div style={{ color: '#EF4444' }}>  ✗ No local stream</div>}
            </div>
            {!Object.keys(stats).length && <div style={{ color: '#4B5563' }}>No peer connections yet</div>}
            {Object.entries(stats).map(([pid, s]) => (
              <div key={pid} style={{ marginBottom: 8, padding: '6px 8px', background: '#111318', borderRadius: 7, borderLeft: `3px solid ${stateColor(s.ice)}` }}>
                <div style={{ fontWeight: 700, color: '#CBD5E1', marginBottom: 3 }}>{s.username}</div>
                <div>ICE: <span style={{ color: stateColor(s.ice), fontWeight: 700 }}>{s.ice}</span>  CONN: <span style={{ color: stateColor(s.conn) }}>{s.conn}</span>  RTT: {s.rtt}</div>
                <div style={{ color: '#6B7280' }}>↑ {s.senders?.join(' ') || 'none'}</div>
                <div style={{ color: '#6B7280' }}>↓ {s.receivers?.join(' ') || 'none'}</div>
                <div style={{ color: s.stream === 'none' ? '#EF4444' : '#22C55E' }}>Stream: {s.stream}</div>
                {s.vWidth > 0 && <div style={{ color: '#F59E0B' }}>Video: {s.vWidth}×{s.vHeight} @ {s.fps}fps</div>}
                <div style={{ color: '#4B5563' }}>TX:{(s.bytesSent/1024).toFixed(1)}KB  RX:{(s.bytesRecv/1024).toFixed(1)}KB  Loss:{s.pktLoss}</div>
              </div>
            ))}
          </>
        )}

        {tab === 'device' && (
          <div style={{ lineHeight: 1.8, color: '#94A3B8' }}>
            <div style={{ color: '#00D4AA', fontWeight: 700, marginBottom: 6 }}>BROWSER / DEVICE</div>
            <div><span style={{ color: '#4B5563' }}>UA: </span>{navigator.userAgent}</div>
            <div><span style={{ color: '#4B5563' }}>Platform: </span>{navigator.platform}</div>
            <div><span style={{ color: '#4B5563' }}>Touch pts: </span>{navigator.maxTouchPoints}</div>
            <div><span style={{ color: '#4B5563' }}>Cores: </span>{navigator.hardwareConcurrency}</div>
            <div><span style={{ color: '#4B5563' }}>Memory: </span>{navigator.deviceMemory ? navigator.deviceMemory + 'GB' : 'n/a'}</div>
            <div><span style={{ color: '#4B5563' }}>Lang: </span>{navigator.language}</div>
            <div><span style={{ color: '#4B5563' }}>Online: </span>{navigator.onLine ? 'yes' : 'no'}</div>
            <div><span style={{ color: '#4B5563' }}>Connection: </span>{navigator.connection ? `${navigator.connection.effectiveType} ${navigator.connection.downlink}Mbps` : 'n/a'}</div>
            <div style={{ color: '#00D4AA', fontWeight: 700, marginTop: 8, marginBottom: 4 }}>WEBRTC SUPPORT</div>
            <div><span style={{ color: '#4B5563' }}>RTCPeerConn: </span>{typeof RTCPeerConnection !== 'undefined' ? '✓' : '✗'}</div>
            <div><span style={{ color: '#4B5563' }}>getUserMedia: </span>{navigator.mediaDevices?.getUserMedia ? '✓' : '✗'}</div>
            <div><span style={{ color: '#4B5563' }}>getDisplayMedia: </span>{navigator.mediaDevices?.getDisplayMedia ? '✓' : '✗'}</div>
            <div style={{ color: '#00D4AA', fontWeight: 700, marginTop: 8, marginBottom: 4 }}>SCREEN</div>
            <div><span style={{ color: '#4B5563' }}>Size: </span>{window.screen.width}×{window.screen.height} ({window.devicePixelRatio}x DPR)</div>
            <div><span style={{ color: '#4B5563' }}>Viewport: </span>{window.innerWidth}×{window.innerHeight}</div>
          </div>
        )}
        <div ref={logEndRef}/>
      </div>

      {/* Action row */}
      <div style={S.row}>
        <button style={S.btn(true)} onClick={copyLog}>Copy Log</button>
        <button style={S.btn(false)} onClick={() => rtcLog.clear()}>Clear</button>
      </div>
    </div>
  );
}

// ── ErrorBoundary ─────────────────────────────────────────────────────────────
export class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0A0C10', color: '#EF4444', gap: 12, padding: 40 }}>
        <div style={{ fontSize: 32 }}>⚠️</div>
        <div style={{ fontWeight: 700, fontSize: 16 }}>Something crashed</div>
        <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#6B7280', maxWidth: 600, textAlign: 'center' }}>{this.state.error?.message}</div>
        <button onClick={() => this.setState({ error: null })} style={{ marginTop: 8, padding: '8px 20px', background: '#1A1D24', border: '1px solid #2A2D36', borderRadius: 8, color: '#CBD5E1', cursor: 'pointer' }}>Dismiss</button>
      </div>
    );
    return this.props.children;
  }
}

// ── MobileCallView ────────────────────────────────────────────────────────────
// Props: channelName, rtc, onLeave, user, chatSlot, onOpenChannels
export function MobileCallView({ channelName, rtc, onLeave, user, chatSlot, onOpenChannels, isAdmin }) {
  const C = useTheme();
  const [expandedId, setExpandedId]   = useState(null);
  const [secs, setSecs]               = useState(0);
  const [chatOpen, setChatOpen]       = useState(false);
  const [cameras, setCameras]         = useState([]);
  const [facingMode, setFacingMode]   = useState('user');
  const [drawerH, setDrawerH]         = useState(340);
  const [showDevices, setShowDevices] = useState(false);
  const drawerRef   = useRef();
  const dragStartY  = useRef(null);
  const drawerStartH = useRef(null);

  useEffect(() => { const t = setInterval(() => setSecs(s => s + 1), 1000); return () => clearInterval(t); }, []);
  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // Detect cameras
  useEffect(() => {
    navigator.mediaDevices.enumerateDevices()
      .then(devs => setCameras(devs.filter(d => d.kind === 'videoinput')))
      .catch(() => {});
  }, [rtc.localStream]);

  const hasMultipleCameras = cameras.length > 1;

  // Flip between front / rear camera
  const flipCamera = async () => {
    const nextFacing = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(nextFacing);
    const tryAcquire = async (exact) => {
      const constraint = exact ? { facingMode: { exact: nextFacing } } : { facingMode: nextFacing };
      return navigator.mediaDevices.getUserMedia({ video: constraint, audio: false });
    };
    try {
      let newStream;
      try { newStream = await tryAcquire(true); }
      catch { newStream = await tryAcquire(false); }
      const newTrack = newStream.getVideoTracks()[0];
      if (!newTrack) return;
      if (rtc._peerConns?.current) {
        for (const pc of Object.values(rtc._peerConns.current)) {
          const sender = pc.getSenders().find(s => s.track?.kind === 'video');
          if (sender) await sender.replaceTrack(newTrack);
        }
      }
      if (rtc.localStream) {
        rtc.localStream.getVideoTracks().forEach(t => { t.stop(); rtc.localStream.removeTrack(t); });
        rtc.localStream.addTrack(newTrack);
      }
    } catch {}
  };

  // Touch drag to resize the chat drawer
  const onHandleTouchStart = e => { dragStartY.current = e.touches[0].clientY; drawerStartH.current = drawerH; };
  const onHandleTouchMove  = e => {
    if (dragStartY.current === null) return;
    const delta = dragStartY.current - e.touches[0].clientY;
    setDrawerH(Math.max(120, Math.min(600, drawerStartH.current + delta)));
  };
  const onHandleTouchEnd = () => {
    if (drawerH < 150) setChatOpen(false);
    dragStartY.current = null;
  };
  const localStream  = rtc.sharing ? rtc.screenStream : rtc.localStream;
  const accentColor  = C.accent || '#00D4AA';
  const tiles        = [
    { id: 'local', stream: localStream, label: user?.username || 'Me', isLocal: true, muted: true },
    ...Object.entries(rtc.peers).map(([pid, p]) => ({ id: pid, stream: p.stream, label: p.username, isLocal: false, muted: false })),
  ];
  const expandedTile = expandedId ? tiles.find(t => t.id === expandedId) : null;
  const thumbTiles   = expandedId ? tiles.filter(t => t.id !== expandedId) : [];

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: '#080A0E', position: 'relative', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', flexShrink: 0, borderBottom: '1px solid #1A1D24' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {onOpenChannels && (
            <button onClick={onOpenChannels} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4B5563', padding: '2px 4px 2px 0', fontSize: 18, lineHeight: 1, display: 'flex', alignItems: 'center' }}>☰</button>
          )}
          <div style={{ color: '#4B5563', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>#{channelName}</div>
        </div>
        <div style={{ color: '#22C55E', fontFamily: 'monospace', fontSize: 13 }}>{fmt(secs)}</div>
      </div>

      {/* Video grid — fills all remaining space above controls */}
      <div style={{ flex: 1, minHeight: 0, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 8, overflow: 'hidden' }}>
        {expandedTile ? (
          <>
            <div style={{ flex: 1, minHeight: 0 }}>
              <VideoTile stream={expandedTile.stream} label={expandedTile.label} isLocal={expandedTile.isLocal} muted={expandedTile.muted} volume={rtc.peerVolumes?.[expandedId]} expanded onShrink={() => setExpandedId(null)} onExpand={() => {}}/>
            </div>
            {thumbTiles.length > 0 && (
              <div style={{ display: 'flex', gap: 8, height: 80, flexShrink: 0 }}>
                {thumbTiles.map(t => <div key={t.id} style={{ width: 120, flexShrink: 0 }}><VideoTile stream={t.stream} label={t.label} isLocal={t.isLocal} muted={t.muted} volume={rtc.peerVolumes?.[t.id]} onExpand={() => setExpandedId(t.id)} onShrink={() => setExpandedId(null)}/></div>)}
              </div>
            )}
          </>
        ) : (
          <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: tiles.length > 1 ? '1fr 1fr' : '1fr', gridTemplateRows: tiles.length > 2 ? '1fr 1fr' : '1fr', gap: 8 }}>
            {tiles.map(t => <VideoTile key={t.id} stream={t.stream} label={t.label} isLocal={t.isLocal} muted={t.muted} onExpand={() => setExpandedId(t.id)} onShrink={() => setExpandedId(null)}/>)}
          </div>
        )}
      </div>

      {/* Control bar */}
      <div style={{ flexShrink: 0, background: '#111318', borderTop: '1px solid #1A1D24', padding: '12px 16px 28px', display: 'flex', justifyContent: 'space-around', alignItems: 'center', gap: 6 }}>
        {/* Cam on/off */}
        <button onClick={rtc.toggleCamera} style={{ width: 52, height: 52, borderRadius: '50%', border: 'none', cursor: 'pointer', background: rtc.camOff ? '#1A1D24' : `${accentColor}22`, color: rtc.camOff ? '#6B7280' : accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <I.Cam off={rtc.camOff}/>
        </button>
        {/* Camera flip — only when multiple cameras present */}
        {hasMultipleCameras && (
          <button onClick={flipCamera} title={facingMode === 'user' ? 'Switch to rear camera' : 'Switch to front camera'}
            style={{ width: 52, height: 52, borderRadius: '50%', border: 'none', cursor: 'pointer', background: '#1A2A28', color: accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
            🔄
          </button>
        )}
        {/* Mute */}
        <button onClick={rtc.toggleMute} style={{ width: 52, height: 52, borderRadius: '50%', border: 'none', cursor: 'pointer', background: rtc.muted ? '#EF444433' : '#1A1D24', color: rtc.muted ? '#EF4444' : '#CBD5E1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <I.Mic off={rtc.muted}/>
        </button>
        {/* Chat toggle */}
        {chatSlot && (
          <button onClick={() => setChatOpen(o => !o)} title={chatOpen ? 'Hide chat' : 'Open chat'}
            style={{ width: 52, height: 52, borderRadius: '50%', border: 'none', cursor: 'pointer', background: chatOpen ? `${accentColor}33` : '#1A1D24', color: chatOpen ? accentColor : '#CBD5E1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </button>
        )}
        {/* Device settings */}
        <button onClick={() => setShowDevices(true)} title="Audio & Device Settings"
          style={{ width: 44, height: 44, borderRadius: '50%', border: '1px solid #2A2D36', cursor: 'pointer', background: '#111318', color: '#CBD5E1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>
          🎛
        </button>
        {/* Leave */}
        <button onClick={onLeave} style={{ width: 62, height: 62, borderRadius: '50%', border: 'none', cursor: 'pointer', background: '#EF4444', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <I.PhoneOff/>
        </button>
        {/* Copy log — admin only */}
        {isAdmin && (
          <button onClick={() => {
            const text = rtcLog.entries.map(e => `[${e.ts}] ${e.msg}`).join('\n');
            navigator.clipboard?.writeText(text).then(() => rtcLog.push('Log copied', 'ok'));
          }} title="Copy RTC log" style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid #2A2D36', cursor: 'pointer', background: '#111318', color: '#4B5563', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}>
            📋
          </button>
        )}
      </div>

      {showDevices && <DeviceSelector rtc={rtc} onClose={() => setShowDevices(false)}/>}

      {/* Slide-up chat drawer */}
      {chatSlot && (
        <div ref={drawerRef} style={{
          position: 'absolute', left: 0, right: 0, bottom: 0,
          height: chatOpen ? drawerH : 0,
          background: '#0D0F14',
          borderTop: chatOpen ? `2px solid ${accentColor}55` : 'none',
          borderRadius: '20px 20px 0 0',
          transition: 'height 0.28s cubic-bezier(0.4,0,0.2,1)',
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          zIndex: 50,
          boxShadow: chatOpen ? '0 -8px 32px rgba(0,0,0,0.7)' : 'none',
        }}>
          {/* Drag handle — pull up/down to resize */}
          <div
            onTouchStart={onHandleTouchStart}
            onTouchMove={onHandleTouchMove}
            onTouchEnd={onHandleTouchEnd}
            style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 0 4px', touchAction: 'none' }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: '#2A2D36' }}/>
          </div>
          {/* Chat content */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            {chatSlot}
          </div>
        </div>
      )}
    </div>
  );
}

// ── iOS autoplay unlock ───────────────────────────────────────────────────────
// iOS requires a user gesture before ANY unmuted video can autoplay.
// We store the unlock state in a module-level variable so VideoTile can check
// it on mount and immediately call play() if already unlocked.
// ── CallPanel ─────────────────────────────────────────────────────────────────
// Props: channelName, rtc, onLeave, chatSlot, chatOpen, setChatOpen, user
export default function CallPanel({ channelName, rtc, onLeave, chatSlot, chatOpen, setChatOpen, user }) {
  const C = useTheme();
  const [secs, setSecs]               = useState(0);
  const [showDebug, setShowDebug]     = useState(false);
  const [showDevices, setShowDevices] = useState(false);
  const [expandedId, setExpandedId]   = useState(null);
  const [chatW, setChatW]             = useState(320);
  const dragRef = useRef(null);
  const gridRef = useRef();

  useEffect(() => { const t = setInterval(() => setSecs(s => s + 1), 1000); return () => clearInterval(t); }, []);
  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const allPeers   = Object.entries(rtc.peers);
  const totalTiles = allPeers.length + 1;
  const cols       = expandedId ? 1 : Math.min(totalTiles, 3);

  useEffect(() => {
    if (!rtc.setVideoBitrate) return;
    if (expandedId)           rtc.setVideoBitrate(2500);
    else if (totalTiles <= 2) rtc.setVideoBitrate(1200);
    else if (totalTiles <= 4) rtc.setVideoBitrate(600);
    else                      rtc.setVideoBitrate(300);
  }, [expandedId, totalTiles]); // eslint-disable-line

  const startDrag = e => {
    e.preventDefault();
    const startX = e.clientX, startW = chatW;
    const onMove = ev => setChatW(Math.max(220, Math.min(700, startW + (ev.clientX - startX))));
    const onUp   = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const localStream = rtc.sharing ? rtc.screenStream : rtc.localStream;
  const tiles = [
    { id: 'local', stream: localStream, label: user && user.username ? user.username : 'Me', isLocal: true, muted: true },
    ...allPeers.map(([pid, p]) => ({ id: pid, stream: p.stream, label: p.username, isLocal: false, muted: false })),
  ];
  const expandedTile = expandedId ? tiles.find(t => t.id === expandedId) : null;
  const thumbTiles   = expandedId ? tiles.filter(t => t.id !== expandedId) : [];

  const controls = [
    { icon: <I.Mic off={rtc.muted}/>,  label: rtc.muted ? 'Unmute' : 'Mute',                  action: rtc.toggleMute,        active: rtc.muted },
    { icon: <I.Cam off={rtc.camOff}/>, label: rtc.camOff ? 'Cam On' : 'Cam Off',               action: rtc.toggleCamera,      active: rtc.camOff },
    { icon: <I.Screen/>,               label: rtc.sharing ? 'Stop Share' : 'Share Screen',      action: rtc.toggleScreenShare, active: rtc.sharing, accent: true },
    { icon: <span style={{ fontSize: 16 }}>🎛</span>, label: 'Devices',                         action: () => setShowDevices(true) },
    { icon: <I.PhoneOff/>,             label: 'Leave',                                           action: onLeave, danger: true },
  ];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'row', minHeight: 0, background: C.bg, position: 'relative' }}>

      {/* Chat panel — LEFT */}
      {chatOpen && (
        <>
          <div style={{ width: chatW, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${C.border}`, background: C.chat, minHeight: 0, overflow: 'hidden' }}>
            <div style={{ padding: '8px 14px', borderBottom: `1px solid ${C.border}`, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <span>💬 Chat</span>
              <button onClick={() => setChatOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 16, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>{chatSlot}</div>
          </div>
          <div ref={dragRef} onMouseDown={startDrag}
            style={{ width: 5, flexShrink: 0, cursor: 'col-resize', background: `linear-gradient(to bottom, ${C.accent}44, ${C.border}, ${C.accent}44)`, transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = C.accent}
            onMouseLeave={e => e.currentTarget.style.background = `linear-gradient(to bottom, ${C.accent}44, ${C.border}, ${C.accent}44)`}
          />
        </>
      )}

      {/* Video area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', height: 46, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ color: C.accent, fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 600 }}>{rtc.sharing ? 'Screen Share' : 'Voice / Video'}</div>
            <div style={{ color: C.muted }}>·</div>
            <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>#{channelName}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {expandedId && <button onClick={() => setExpandedId(null)} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}>← Grid</button>}
            <button onClick={() => setShowDebug(d => !d)} style={{ background: showDebug ? '#EF444422' : 'none', border: `1px solid ${showDebug ? '#EF4444' : C.border}`, borderRadius: 6, color: showDebug ? '#EF4444' : C.muted, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}>🔬</button>
            <button onClick={() => setChatOpen(o => !o)} style={{ background: chatOpen ? `${C.accent}22` : C.card, border: `1px solid ${chatOpen ? C.accent : C.border}`, borderRadius: 6, color: chatOpen ? C.accent : C.muted, padding: '3px 10px', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
              💬 {chatOpen ? 'Hide Chat' : 'Show Chat'}
            </button>
            <div style={{ color: '#22C55E', fontFamily: 'monospace', fontSize: 13 }}>{fmt(secs)}</div>
          </div>
        </div>

        {/* Tiles */}
        {expandedTile ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, padding: 12, gap: 8 }}>
            <div style={{ flex: 1, minHeight: 0 }}>
              <VideoTile stream={expandedTile.stream} label={expandedTile.label} isLocal={expandedTile.isLocal} muted={expandedTile.muted} volume={rtc.peerVolumes?.[expandedId]} expanded onShrink={() => setExpandedId(null)} onExpand={() => {}}/>
            </div>
            {thumbTiles.length > 0 && (
              <div style={{ display: 'flex', gap: 8, height: 90, flexShrink: 0, overflowX: 'auto' }}>
                {thumbTiles.map(t => <div key={t.id} style={{ width: 140, flexShrink: 0 }}><VideoTile stream={t.stream} label={t.label} isLocal={t.isLocal} muted={t.muted} volume={rtc.peerVolumes?.[t.id]} onExpand={() => setExpandedId(t.id)} onShrink={() => setExpandedId(null)}/></div>)}
              </div>
            )}
          </div>
        ) : (
          <div ref={gridRef} style={{ flex: 1, padding: 12, display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 10, alignContent: 'center', overflow: 'auto' }}>
            {tiles.map(t => <VideoTile key={t.id} stream={t.stream} label={t.label} isLocal={t.isLocal} muted={t.muted} onExpand={() => setExpandedId(t.id)} onShrink={() => setExpandedId(null)}/>)}
          </div>
        )}

        {/* Controls */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, padding: '10px 0 16px', flexShrink: 0, borderTop: `1px solid ${C.border}` }}>
          {controls.map(b => (
            <div key={b.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              <button onClick={b.action} title={b.label} style={{ width: 46, height: 46, borderRadius: '50%', border: 'none', cursor: 'pointer', background: b.danger ? '#EF4444' : b.active ? (b.accent ? C.accent : '#EF444433') : C.card, color: b.danger || (b.accent && b.active) ? '#0A0C10' : C.text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{b.icon}</button>
              <span style={{ fontSize: 10, color: C.muted }}>{b.label}</span>
            </div>
          ))}
        </div>

        {showDebug   && <RtcDebugPanel rtc={rtc}/>}
        {showDevices && <DeviceSelector rtc={rtc} onClose={() => setShowDevices(false)}/>}
      </div>
    </div>
  );
}
